<template>
  <div>
    <alert-modal
      ref="confirm"
      cancelable="true"
      :alertText="alertText"
      @confirm="confirmedDeleteBooking"
      @cancel="cancelDeleteBooking"
    />

    <picture-modal
      ref="modal"
      :modal-class="isChannexBooking ? 'booking--locked' : ''"
      :views="views"
      :overrideTitle="bookingTitle"
      :config="config"
      @hide="hide"
    >
      <!-- Tab one of booking Modal -->
      <template slot="tabOne">
        <!-- We'll need some sort of drag and dropping going on here.
        Maybe build "grouping" and "ordering" into the taggregator? Ordering may come in handy for things like-->
        <b-form
          class="booking-form"
          autocomplete="off"
          @submit.prevent="onSubmit"
        >
          <info-row
            class="guest-count-row info-main-row"
            :title="$t('booking_modal.guest_count')"
            :size="4"
          >
            <b-form-input
              v-model="guestCount"
              id="guest-count"
              class="input-info-field"
              type="text"
            />
          </info-row>

          <Taggregator
            ref="addGuestsTags"
            name="guests"
            placeholder="Add Guest"
            :inputId="'add-guest-taggregator'"
            :items="contacts"
            :selections.sync="bookingGuests"
            :onAdd="addGuest"
            :onRemove="removeGuest"
            :minimum="1"
            :quickAddCallback="quickAddGuest"
            :quickAddDescription="'Please provide valid first and last names'"
            :createItemCallback="showCreateGuest"
            :disabled="isChannexBooking"
          >
            <template slot="result" slot-scope="{ result }">
              <span class="result-label">
                {{ result.label }}
                <i class="result-sub-label">
                  {{ result.email ? `(${result.email})` : null }}
                </i>
              </span>
            </template>

            <template slot="selection" slot-scope="{ selection }">
              <div class="card-text room-card">
                <!-- @click="$refs.newGuestModal.show(getContact(selection.id))"> -->
                <!-- Button to assign a guest leader -->
                <button
                  class="main-guest-button btn btn-xs"
                  :class="{ active: isMainGuest(selection) }"
                  @click="setMainGuest(selection)"
                >
                  <i
                    v-if="!isMainGuest(selection)"
                    class="fi fi-torso small"
                    v-b-tooltip.hover.top="'Make Booking Owner'"
                  />
                  <i
                    v-else
                    class="fi fi-crown small"
                    v-b-tooltip.hover.top="'Booking Owner'"
                  />
                </button>

                <!-- Displays Guest Name -->
                <span class="reservation-label">{{ selection.label }}</span>

                <!-- Email Button to copy to clipboard -->
                <button
                  v-if="selection.email"
                  id="copy_email_button"
                  class="phone-guest-button btn btn-xs fi-mail"
                  v-clipboard:copy="selection.email"
                  v-b-tooltip.hover.top="
                    'Click To Copy Email: ' + selection.email
                  "
                  v-clipboard:success="emailOnCopy"
                  v-clipboard:error="emailOnError"
                />

                <!-- Phone Number Button to copy to clipboard -->
                <button
                  v-if="selection.phone"
                  id="copy_phone_number_button"
                  class="phone-guest-button btn btn-xs fi-telephone"
                  v-clipboard:copy="selection.phone"
                  v-b-tooltip.hover.top="
                    'Click To Copy Phone Number: ' + selection.phone
                  "
                  v-clipboard:success="phoneOnCopy"
                  v-clipboard:error="phoneOnError"
                />
              </div>

              <!-- Guest Room Assignment -->
              <div class="align-room-select-right">
                <b-form-select
                  v-if="bookingRooms.length > 1 && booking.reservations"
                  class="form-control"
                  value-field="reservation_id"
                  v-b-tooltip.hover.top="'Assign Guest To Room'"
                  text-field="label"
                  :value="guestRoomTable[selection.id]"
                  :options="bookingRooms"
                  @change="onGuestRoomUpdate($event, selection.id)"
                >
                  <!-- <option slot="first" value="0">-- Select Room</option> -->
                </b-form-select>
                <div v-else-if="bookingRooms.length == 1" class="pt-1">
                  {{ bookingRooms[0].label }}
                </div>
              </div>
            </template>
          </Taggregator>
        </b-form>

        <div class="payments-spinner" v-if="roomsUpdating">
          <b-spinner label="Loading..."></b-spinner>
        </div>
      </template>

      <!-- Tab Two of Booking Modal -->
      <template slot="tabTwo">
        <b-form
          class="booking-form"
          autocomplete="off"
          @submit.prevent="onSubmit"
        >
          <Taggregator
            v-if="!loadingUnits || loadingTimedOut"
            :inputId="'add-room-taggregator'"
            :placeholder="'Add Room'"
            :items="rooms"
            :selections.sync="bookingRooms"
            :onRemove="removeRoom"
            :onAdd="addRoom"
            :name="'rooms'"
            :minimum="1"
            :disabled="isChannexBooking"
          >
            <template slot="result" slot-scope="{ result }">
              <span class="result-label">
                {{ result.label }}
                <i class="result-sub-label">
                  {{ result.unitType ? `(${result.unitType})` : null }}
                </i>
                <span class="result-right-label">
                  {{
                    result.total_rate
                      ? `${getVendor.local_currency}${result.total_rate} Total`
                      : null
                  }}
                </span>
              </span>
            </template>

            <template slot="selection" slot-scope="{ selection }">
              <!-- Virtual Key Door Lock Code -->
              <span>
                <button
                  v-if="selection.doorCode"
                  id="copy_phone_number_button"
                  class="door-lock-button btn btn-xs fi-lock"
                  v-clipboard:copy="selection.doorCode"
                  v-b-tooltip.hover.top="
                    'Click To Copy Lock Code: ' + selection.doorCode
                  "
                  v-clipboard:success="lockOnCopy"
                  v-clipboard:error="lockOnError"
                />
              </span>

              <div class="card-text room-card text-center booking-room-list">
                <span class="reservation-label room-label">
                  {{ selection.label }}
                </span>

                <span class="reservation-dates">
                  <v-date-picker
                    class="daterange-picker"
                    popover-direction="bottom"
                    popover-align="center"
                    mode="range"
                    show-caps
                    :disabled-dates="getDisabledDates(selection.id, selection)"
                    :disabled-attribute="disabledAttributes"
                    :drag-attribute="dragAttributes"
                    :select-attribute="selectAttributes"
                    :theme-styles="themeStyles"
                    :value="getRoomDateRange(selection)"
                    @input="(v) => setRoomDateRange(v, selection)"
                  />
                  {{
                    formatBookingPeriod(
                      selection.startDate || newReservationStart
                    )
                  }}
                  -
                  {{
                    formatBookingPeriod(selection.endDate || newReservationEnd)
                  }}
                </span>
                <!-- TODO: Enable this for creating res -->
                <span
                  v-if="booking.id"
                  class="reservation-rate"
                  v-b-tooltip.hover.top="'Edit Room Rate'"
                  @click="passToRoomRateModal(selection)"
                >
                  {{ getVendor.local_currency }}{{ rateDisplay(selection) }}
                  <i v-if="!isChannexBooking" class="fi-pencil" />
                </span>

                <span v-else class="reservation-rate">
                  {{ getVendor.local_currency }}{{ rateDisplay(selection) }}
                </span>
                <!-- TODO: Enable this for creating res -->
              </div>
            </template>
          </Taggregator>
          <div class="accoms-spinner" v-if="loadingUnits && !loadingTimedOut">
            <b-spinner label="Loading..."></b-spinner>
          </div>
        </b-form>
      </template>

      <!-- Tab Three of Booking Modal-->
      <template slot="tabThree" v-if="bookingRooms.length > 0">
        <b-form>
          <div id="go-to-payments">
            <b-button
              variant="secondary"
              :disabled="!bookingTab || !bookingTab.id"
              @click="goToPayments"
              v-b-tooltip.hover.top="'Navigate to Guest Tab Section'"
            >
              {{ $t("booking_modal.goto_payments") }}
            </b-button>
          </div>

          <div v-if="paymentsLoading" class="payments-spinner">
            <b-spinner label="Loading..." />
          </div>

          <div v-else-if="bookingTab && bookingTab.id" class="billing-summary">
            <div class="card mt-3">
              <div class="card-header">
                <strong>Billing Summary</strong>
              </div>
              <div class="card-body">
                <div class="row mb-2">
                  <div class="col-6 text-left">Invoice Number:</div>
                  <div class="col-6 text-right">{{ bookingTab.id }}</div>
                </div>
                <div class="row mb-2">
                  <div class="col-6 text-left">Balance Due:</div>
                  <div class="col-6 text-right">
                    {{ formatMoney(bookingTab.stats.balance_due || 0) }}
                  </div>
                </div>
                <div class="row mb-2" v-if="bookingTab.stats.subtotal">
                  <div class="col-6 text-left">Subtotal:</div>
                  <div class="col-6 text-right">
                    {{ formatMoney(bookingTab.stats.subtotal || 0) }}
                  </div>
                </div>
                <div class="row" v-if="bookingTab.stats.card_hold">
                  <div class="col-6 text-left">Card Hold:</div>
                  <div class="col-6 text-right">
                    {{ formatMoney(bookingTab.stats.card_hold || 0) }}
                  </div>
                </div>
              </div>
              <div class="card-footer text-center">
                <button
                  class="btn btn-outline-primary btn-sm"
                  @click="goToPayments"
                  :disabled="!bookingTab.id"
                >
                  Open Full Billing Page
                </button>
              </div>
            </div>
          </div>

          <div v-else class="text-center mt-4">
            <p>
              {{
                $t("booking_modal.save_booking_to_create_payment") ||
                "Please save booking first to access payment options"
              }}
            </p>
          </div>
        </b-form>
      </template>

      <!-- Tab Four of Booking Modal -->
      <template slot="tabFour">
        <b-form-group
          class="booking-notes"
          label-for="notesTextArea"
          :label="$t('booking_modal.reservation_special_requests')"
        >
          <b-form-textarea
            v-model="formData.notes"
            id="notesTextArea"
            rows="2"
            type="text"
          />
        </b-form-group>

        <booking-package-form
          ref="packageForm"
          :enabled="packageEnabled"
          :packageName.sync="packageName"
          :packageRate.sync="packageRate"
          :packageType.sync="packageType"
          @toggle="togglePackages"
          @update="
            (newName) => {
              packageName = newName;
            }
          "
        />
      </template>

      <!-- tab five of booking -->
      <template slot="tabFive">
        <div v-if="paymentsLoading">
          <b-spinner label="Loading..."></b-spinner>
        </div>

        <div v-if="!paymentsLoading && bookingTab.id">
          <b-form-group label-for="booking_notes">
            <b-form-textarea
              v-model="newBookingNote"
              id="booking_notes"
              :placeholder="$t('contact_modal.new_note')"
              rows="2"
              type="text"
            />
          </b-form-group>

          <b-button
            class="note-button"
            variant="outline-secondary"
            @click="addBookingNote"
            v-b-tooltip.hover.top="'Add note to Profile'"
          >
            {{ $t("contact_modal.add_note") }}
          </b-button>

          <b-card
            bg-variant="default"
            v-for="(bookingNote, index) in bookingNotes"
            :key="index"
          >
            <b-card-text class="pre-line">{{
              bookingNote.content
            }}</b-card-text>

            <template v-slot:header>
              <small class="text-muted"
                >Note Created: {{ prettyDate(bookingNote.created_at) }}
                <i
                  class="delete-icon float-right fi fi-x"
                  v-b-tooltip.hover.top="'Remove'"
                  @click="removeBookingNote(bookingNote.id)"
                />
              </small>
            </template>
          </b-card>
        </div>

        <div v-if="!paymentsLoading && !bookingTab.id">
          <p>{{ $t("booking_modal.save_booking_to_create_note") }}</p>
        </div>
      </template>

      <!-- Booking Modal Tool Buttons -->
      <template slot="modal-toolbar-buttons">
        <!-- Check-in Button -->
        <span class="status-icons booking-action-btn">
          <i
            v-if="onHold && (!bookingTab || !bookingTab.id)"
            class="nav-icon booking-status disabled"
          >
            <img :src="checkinIcon" class="status-icon check-in-icon" />
          </i>

          <i
            v-if="onHold && bookingTab && bookingTab.id"
            class="nav-icon booking-status"
          >
            <img
              :src="checkinIcon"
              class="status-icon check-in-icon"
              @click="showCheckInModal"
              v-b-tooltip.hover.top="'Check-In'"
            />
          </i>

          <!-- Check-Out Button-->
          <i
            v-if="checkedIn && (!bookingTab || !bookingTab.id)"
            class="nav-icon booking-status disabled"
          >
            <img :src="checkoutIcon" class="status-icon check-out-icon" />
          </i>

          <i
            v-if="checkedIn && bookingTab && bookingTab.id"
            class="nav-icon booking-status"
          >
            <img
              :src="checkoutIcon"
              class="status-icon check-out-icon"
              @click="showCheckOutModal"
              v-b-tooltip.hover.top="'Check-Out'"
            />
          </i>
        </span>

        <!-- Other Tool Buttons -->
        <span
          v-if="formData.id"
          class="booking-action-btn"
          @click="deleteBooking"
        >
          <i v-if="!isConnected" class="backup-icon">Delete</i>
          <i
            v-else
            class="fi-trash delete-booking nav-icon modal-btn"
            v-b-tooltip.hover.top="'Delete Booking'"
          />
        </span>

        <span class="booking-action-btn" @click="saveBooking">
          <i v-if="!isConnected" class="backup-icon">Save</i>
          <i
            v-else
            class="fi-save create-booking nav-icon modal-btn"
            v-b-tooltip.hover.top="'Save Booking'"
          />
        </span>

        <span class="modal-btn" @click="hide">
          <i v-if="!isConnected" class="backup-icon">Cancel</i>
          <i
            v-else
            class="fi-x cancel-booking nav-icon"
            v-b-tooltip.hover.top="'Close Window'"
          />
        </span>
      </template>
    </picture-modal>

    <contact-modal
      ref="newGuestModal"
      class="contact-modal-btn-new"
      :addGuest="createGuest"
      :cancel="returnToBooking"
    />

    <check-in-modal ref="checkInModal" @checkin="checkIn" @cancel="checkIn" />

    <check-out-modal ref="checkOutModal" @checkout="checkOut" />
  </div>
</template>

<script>
import { mapGetters, mapActions, mapState } from "vuex";
import { BOOKING_STATUS } from "@/config/bookings";
import { PAYMENT_TYPES, RESERVATION_RATE_TYPES } from "@/config/constants";
import { formatBookingPeriod } from "@/utils/date-helper";
import { format } from "url";
import cloneDeep from "lodash.clonedeep";
import AlertMixin from "@/mixins/alert-mixin";
import PricingMixin from "@/mixins/pricing-mixin";
import { formatNoteDateTime } from "@/utils/date-helper";
import checkinIcon from "@/assets/icons/checkin.svg";
import checkoutIcon from "@/assets/icons/checkout.svg";

export default {
  name: "booking-modal",
  components: {
    PictureModal: () => import("@/components/modals/PictureModal"),
    Taggregator: () => import("@/components/Taggregator"),
    AlertModal: () => import("@/components/modals/AlertModal"),
    ContactModal: () => import("@/components/modals/ContactModal"),
    CheckInModal: () => import("@/components/modals/CheckInModal"),
    CheckOutModal: () => import("@/components/modals/CheckOutModal"),
    InfoRow: () => import("@/components/InfoRow"),
    ReservationPaymentForm: () =>
      import("@/components/forms/ReservationPaymentForm"),
    BookingPackageForm: () => import("@/components/forms/BookingPackageForm"),
  },
  mixins: [AlertMixin, PricingMixin],
  data() {
    return {
      checkinIcon,
      checkoutIcon,
      query: "",
      paymentsLoading: false,
      contacts: [],
      rooms: [],
      bookingNotes: [],
      newBookingNote: "",
      currentId: 0,
      guestRoomTable: {},
      formData: this.blankForm(),
      reservationState: {},
      bookingRooms: [],
      roomsUpdating: false,
      bookingGuests: [],
      guestCount: 0,
      creatingGuest: false,
      mainGuestId: null,
      bookingStatus: 0,
      bookingTab: {
        id: null,
        guest_id: null,
        stats: {
          balance_due: 0,
          card_hold: 0,
          subtotal: 0,
        },
      },
      loadingUnits: false,
      packageEnabled: false,
      packageName: "",
      packageType: "",
      packageRate: "",
      config: {
        title: "New Booking",
        pictureUrl: null,
      },
      views: [
        {
          title: this.$t("booking_modal.tabOne_title"),
          id: "tabOne",
        },
        {
          title: this.$t("booking_modal.tabTwo_title"),
          id: "tabTwo",
        },
        {
          title: this.$t("booking_modal.tabThree_title"),
          id: "tabThree",
        },
        {
          title: this.$t("booking_modal.tabFour_title"),
          id: "tabFour",
        },
        {
          title: this.$t("booking_modal.tabFive_title"),
          id: "tabFive",
        },
      ],
      newReservationStart: "",
      newReservationEnd: "",
      disabledDates: {},
      disabledAttributes: {
        contentStyle: {
          backgroundColor: "#f7bbb9",
          color: "#333",
          cursor: "not-allowed",
          fontWeight: "400",
          borderRadius: "3px",
        },
      },
      dragAttributes: {
        contentStyle: {
          backgroundColor: "transparent",
          color: "inherit",
          fontWeight: "400",
          borderRadius: "0",
        },
      },
      selectAttributes: {
        contentStyle: {
          backgroundColor: "transparent",
          color: "inherit",
          fontWeight: "400",
          borderRadius: "3px",
        },
      },
      themeStyles: {
        dayContent: {
          backgroundColor: "#dcffcc",
          color: "#333",
          fontWeight: "400",
          borderRadius: "3px",
        },
      },
      loadingTimedOut: false,
      allRooms: [],
    };
  },

  computed: {
    ...mapGetters([
      "getVendor",
      "getBookingStatuses",
      "getBookingRateTypes",
      "getReservedDates",
      "getUnits",
      "getUnitTypesList",
      "getContacts",
      "getContact",
      "getAvailableUnitsByDate",
      "getBookings",
      "isConnected",
    ]),
    ...mapState({
      roomsLoading: (state) => state.accommodations.isRequesting,
      isRequesting: (state) => state.bookingNotes.isRequesting,
    }),
    alertText() {
      return `Are you sure you want to delete this booking (${this.formData.booking_name})?`;
    },
    reservationDuration() {
      return this.$moment(this.formData.ends_at).diff(
        this.$moment(this.formData.starts_at),
        "days"
      );
    },
    onHold() {
      return this.bookingStatus === BOOKING_STATUS.HOLD;
    },
    checkedIn() {
      return this.bookingStatus === BOOKING_STATUS.CHECKED_IN;
    },
    checkedOut() {
      return this.bookingStatus === BOOKING_STATUS.CHECKED_OUT;
    },
    bookingTitle() {
      return this.packageEnabled ? this.packageName : this.config.title;
    },
    isChannexBooking() {
      return this.formData && !!this.formData.is_channex_reservation;
    },
  },

  mounted() {
    this.target = this.$refs.input;
    console.log("Loaded components:", {
      CheckInIcon: this.$options.components.CheckInIcon,
      CheckOutIcon: this.$options.components.CheckOutIcon,
      PictureModal: this.$options.components.PictureModal,
    });
  },

  methods: {
    ...mapActions([
      "updateBooking",
      "addContact",
      "addGuestToBooking",
      "addReservation",
      "updateReservation",
      "removeReservation",
      "removeGuestFromBooking",
      "getFilteredTab",
      "fetchAvailableUnitsByDate",
      "removeBooking",
      "addBooking",
      "fetchBookings",
      "fetchReservedDates",
      "createBookingNote",
      "deleteBookingNote",
      "getBookingNotes",
    ]),
    async show(selectedReservation) {
      // Start global progress bar while the booking modal data loads
      this.$Progress.start();
      console.log(
        "Opening booking modal for reservation:",
        selectedReservation
      );
      this.reservationState = selectedReservation;
      this.booking = {};
      this.paymentsLoading = true; // Start loading payments early
      this.bookingTab = {}; // Clear booking tab data

      // Step 1: Populate form with basic data
      this.populateForm(selectedReservation);

      // Step 2: Initialize allRooms with all available units
      this.allRooms = this.taggregateUnits(cloneDeep(this.getUnits));
      console.log("Available rooms initialized:", this.allRooms.length);

      // Step 3: Prepare taggregators for guests and rooms
      await this.prepareTaggregators(selectedReservation);

      if (selectedReservation.booking_id) {
        try {
          console.log(
            "Processing existing booking ID:",
            selectedReservation.booking_id
          );

          // Step 4: Get the booking data from store
          const booking = this.getBookings.find(
            (booking) => booking.id === selectedReservation.booking_id
          );

          if (!booking) {
            console.error("Booking not found:", selectedReservation.booking_id);
            this.paymentsLoading = false;
          } else {
            console.log("Booking found:", booking);
            this.booking = booking;

            // Step 5: Extract booking data
            this.formData.notes = booking.notes;
            this.bookingStatus = booking.booking_status_id;

            // Step 6: Get guest data and image
            const guest = this.getContact(booking.guest_id);
            this.config.pictureUrl = guest
              ? guest.photo_md_uri || guest.photo_uri
              : "";

            // Step 7: Populate rooms data
            if (booking.reservations && booking.reservations.length > 0) {
              console.log(
                "Booking has reservations:",
                booking.reservations.length
              );
              this.bookingRooms = []; // Clear rooms first
              booking.reservations.forEach((bookedReservation) => {
                this.reserveRoom(bookedReservation);
              });
              console.log("Rooms populated:", this.bookingRooms.length);
            } else {
              console.warn("Booking has no reservations");
            }

            // Step 8: Load tab/payment data
            try {
              const mainGuestId = booking.guest_id;
              if (mainGuestId) {
                this.mainGuestId = mainGuestId;
                const booking_id = booking.id;

                console.log(
                  "Fetching tab data for booking:",
                  booking_id,
                  "guest:",
                  mainGuestId
                );

                // Initialize default tab structure to prevent errors
                this.bookingTab = {
                  id: null,
                  guest_id: mainGuestId,
                  stats: {
                    balance_due: 0,
                    card_hold: 0,
                    subtotal: 0,
                  },
                };

                // Fetch tab data
                const tabData = await this.getFilteredTab({
                  booking_id,
                  guest_id: mainGuestId,
                });

                console.log("Tab data received:", tabData);

                if (tabData && Object.keys(tabData).length > 0) {
                  // Ensure stats object exists
                  if (!tabData.stats) {
                    tabData.stats = {
                      balance_due: 0,
                      card_hold: 0,
                      subtotal: 0,
                    };
                  }

                  this.bookingTab = tabData;
                } else {
                  console.warn(
                    "No tab data returned or empty object, using fallback"
                  );
                  this.bookingTab.id = `tab-${booking_id}`;
                }
              } else {
                console.warn("No main guest ID found for booking");
                this.bookingTab = {
                  id: null,
                  guest_id: null,
                  stats: {
                    balance_due: 0,
                    card_hold: 0,
                    subtotal: 0,
                  },
                };
              }
            } catch (error) {
              console.error("Error fetching tab data:", error);
              // Create fallback tab data
              this.bookingTab = {
                id: `tab-${booking.id}`,
                guest_id: booking.guest_id,
                stats: {
                  balance_due: 0,
                  card_hold: 0,
                  subtotal: 0,
                },
              };
            }

            // Step 9: Populate guests data
            this.populateGuests(selectedReservation);
          }
        } catch (error) {
          console.error("Error populating booking data:", error);
        } finally {
          this.paymentsLoading = false;
        }
      } else {
        // New booking
        this.reserveRoom(selectedReservation);
        this.paymentsLoading = false;
      }

      // Step 10: Fetch reserved dates for calendar
      this.fetchReservedDates({
        booking_id: selectedReservation.booking_id,
      });

      // Step 11: Show the modal
      this.$refs.modal.show();

      // Step 12: Populate package info and guest room table
      this.populatePackageInfo();
      this.makeGuestRoomTable();

      // Step 13: Load booking notes
      this.currentId = selectedReservation.booking_id;
      if (this.currentId) {
        try {
          let bookingNotes = await this.getBookingNotes(this.currentId);

          function filterByProperty(array, value) {
            var filtered = [];
            for (var i = 0; i < array.length; i++) {
              var obj = array[i];
              if (obj.booking_id == value) {
                filtered.push(obj);
              }
            }
            return filtered;
          }

          var filteredByBookingId = filterByProperty(
            bookingNotes,
            this.currentId
          );
          this.bookingNotes = filteredByBookingId.reverse();
        } catch (error) {
          console.error("Error loading booking notes:", error);
          this.bookingNotes = [];
        }
      }

      // Step 14: Set booking name
      if (
        selectedReservation &&
        selectedReservation.booking_name &&
        selectedReservation.booking_name !== this.config.title
      ) {
        this.packageEnabled = true;
        this.packageName = selectedReservation.booking_name;
      }

      console.log(
        "Booking modal fully loaded. Rooms:",
        this.bookingRooms.length,
        "Tab data:",
        !!this.bookingTab.id
      );
      // Finish progress bar now that everything is ready
      this.$Progress.finish();
    },
    async addBookingNote() {
      if (!this.newBookingNote) {
        this.errorNotification(
          this.$t("booking_modal.notification_blank_note_attempt")
        );
        return;
      }
      const bookingNote = {
        booking_id: this.currentId,
        content: this.newBookingNote,
      };
      const newNote = await this.createBookingNote(bookingNote);
      this.bookingNotes.unshift(newNote);
      this.newBookingNote = "";
      this.successNotification(
        this.$t("booking_modal.notification_note_added")
      );
    },
    async removeBookingNote(bookingNoteId) {
      await this.deleteBookingNote(bookingNoteId);
      const deletedRecord = this.bookingNotes.find(
        (bookingNote) => bookingNote.id === bookingNoteId
      );
      const currentIndex = this.bookingNotes.indexOf(deletedRecord);
      this.bookingNotes.splice(currentIndex, 1);
      this.successNotification(
        this.$t("booking_modal.notification_note_deleted")
      );
    },
    prettyDate(date) {
      return formatNoteDateTime(date);
    },
    onGuestRoomUpdate(_reservationId, _guestId) {
      // Do this quickly for the UI
      this.roomsUpdating = true;
      this.$nextTick(() => {
        const oldReservation = this.booking.reservations.find(
          (reservation) => reservation.id === this.guestRoomTable[_guestId]
        );
        this.guestRoomTable[_guestId] = _reservationId;
        let newReservation = this.booking.reservations.find(
          (reservation) => _reservationId === reservation.id
        );

        this.$store
          .dispatch("addGuestToReservation", {
            guest_id: _guestId,
            reservation_id: newReservation.id,
          })
          .then((_response) => {
            if (oldReservation) {
              let oldGuestReservation = oldReservation.guests.find(
                (guest) => guest.guest_id === _guestId
              );
              if (oldGuestReservation) {
                this.$store
                  .dispatch("removeGuestFromReservation", oldGuestReservation)
                  .then((_res) => {
                    this.roomsUpdating = false;
                    this.successNotification("Updated Guest Room Assignment");
                  });
              }
            } else {
              this.roomsUpdating = false;
              this.successNotification("Updated Guest Room Assignment");
            }
          });
      });
    },
    // Make a table to hinge reservation ID and guest... otherwise, it's really annoying
    makeGuestRoomTable() {
      this.guestRoomTable = {};
      if (!this.booking.reservations) {
        return;
      }
      this.booking.reservations.forEach((bookedReservation) => {
        bookedReservation.guests.forEach((guest) => {
          this.guestRoomTable[guest.guest_id] = bookedReservation.id;
        });
      });
    },
    togglePackages(isToggled) {
      this.packageEnabled = isToggled;

      if (this.packageEnabled) {
        this.packageName = this.booking.name;
        this.packageType = this.booking.package_id;
        this.packageRate = this.booking.rate;
      } else {
        this.packageName = "";
        this.packageType = "";
        this.packageRate = "";
        this.resetBookingName();
      }
    },
    passToRoomRateModal(selection) {
      let reservation = {};

      if (this.booking.reservations && this.booking.reservations.length > 0) {
        reservation = this.booking.reservations.find(
          (reservation_) => reservation_.unit_id === selection.id
        );

        reservation = reservation || {};
      }

      let rate = this.reservationRate(selection);

      this.$parent.$refs.roomRateSelector.setReservation(reservation, rate);
      this.$parent.$refs.roomRateSelector.show();
    },
    rateDisplay(selection) {
      return `${this.reservationRate(selection)} Total`;
    },
    reservationRate(selection) {
      if (this.booking && this.booking.reservations) {
        const reservation =
          this.booking.reservations.find(
            (reservation_) => reservation_.unit_id === selection.id
          ) || {};

        if (reservation.rate_type_id === 2) {
          return reservation.rate;
        }

        let rate_sum = 0;

        reservation.pages.map((page) => {
          rate_sum += reservation.rate ? reservation.rate : page.rate;
        });

        return Math.round(rate_sum * 100) / 100;
      }

      return this.getReservationRate(
        selection.id,
        selection.rate,
        selection.startDate,
        selection.endDate
      );
    },
    async showCheckInModal() {
      if (!!this.getVendor.stripe_id) {
        let guest = this.getContacts.find(
          (guest_) => guest_.id === this.mainGuestId
        );
        if (guest && this.bookingTab && this.bookingTab.id) {
          this.$refs.checkInModal.show(guest, this.bookingTab);
        }
      } else {
        await this.checkIn();
      }
    },
    async checkIn() {
      try {
        await this.updateBooking({
          id: this.booking.id,
          booking_status_id: BOOKING_STATUS.CHECKED_IN,
        });
        this.successNotification(`${this.bookingTitle} booking checked in`);
        this.bookingStatus = BOOKING_STATUS.CHECKED_IN;
        this.$refs.checkInModal.hide();
      } catch (e) {
        this.errorNotification(
          `Failed to check in booking ${this.bookingTitle}: ${e.message}`
        );
      }
    },
    showCheckOutModal() {
      if (
        this.bookingTab &&
        this.bookingTab.stats &&
        this.bookingTab.stats.card_hold > 0
      ) {
        this.$refs.checkOutModal.show(this.bookingTab.stats.card_hold);
      } else {
        this.showConfirm("Do you want to check this guest out?", "info").then(
          (result) => {
            if (result.value) {
              this.checkOut();
            }
          }
        );
      }
    },
    async checkOut() {
      try {
        await this.updateBooking({
          id: this.booking.id,
          booking_status_id: BOOKING_STATUS.CHECKED_OUT,
        });
        this.successNotification(`${this.bookingTitle} booking checked out`);
        this.bookingStatus = BOOKING_STATUS.CHECKED_OUT;
        this.$refs.checkOutModal.hide();
      } catch (e) {
        this.errorNotification(
          `Failed to check out booking ${this.bookingTitle}: ${e.message}`
        );
      }
    },
    lockOnCopy(e) {
      this.infoNotification("Door Lock Code Copied To Clipboard.");
    },
    lockOnError(e) {
      this.errorNotification(
        "Failed To Copy Door Lock Code, Please Try Again."
      );
    },
    phoneOnCopy(e) {
      this.infoNotification("Phone Number Copied To Clipboard.");
    },
    phoneOnError(e) {
      this.errorNotification("Failed To Copy Phone Number, Please Try Again.");
    },
    emailOnCopy(e) {
      this.infoNotification("Email Address Copied To Clipboard.");
    },
    emailOnError(e) {
      this.errorNotification("Failed To Email Address, Please Try Again.");
    },
    goToPayments(data) {
      this.$router.push(`tabs?tab_id=${this.bookingTab.id}`);
    },
    renderBooking(booking) {
      this.formData = booking || this.formData;
      this.$refs.modal.show();
    },
    hide() {
      this.formData = this.blankForm();
      this.bookingRooms = [];
      this.bookingStatus = 0;
      this.bookingGuests = [];
      this.bookingTab = {
        id: null,
        guest_id: null,
        stats: {
          balance_due: 0,
          card_hold: 0,
          subtotal: 0,
        },
      };

      this.creatingGuest = false;
      this.selectedReservation = {};
      this.mainGuestId = null;
      this.guestCount = null;
      this.config.title = "";
      this.config.pictureUrl = null;
      this.packageEnabled = false;
      this.packageName = "";
      this.bookingNotes = [];
      this.newBookingNote = "";

      if (this.$refs.modal) {
        this.$refs.modal.hideModal();
      }
    },
    getLabel(item) {
      return item;
    },

    quickAddGuest(newGuestName) {
      const newGuest = {
        first_name: newGuestName.split(" ")[0],
        last_name: newGuestName.split(" ")[1],
      };

      if (newGuest.first_name && newGuest.last_name) {
        return this.addContact(newGuest)
          .then((newContact) => {
            const newGuest = {
              id: newContact.id,
              label: `${newContact.first_name} ${newContact.last_name}`,
              image: newContact.photo_md_uri || newContact.photo_uri,
            };
            this.bookingGuests.push(newGuest);
            this.addGuest(newGuest);
          })
          .catch((error) => {
            this.errorNotification(`Quick Add Failed - ${error}`);
          });
      } else {
        this.errorNotification("Quick Add Failed - Invalid Name");
      }
      this.renderBooking();
    },
    showCreateGuest() {
      this.currentBooking = this.formData;
      this.currentGuests = this.bookingGuests; // showing modal will clear form, hold data here
      this.currentRooms = this.bookingRooms;
      this.currentLeadGuest = this.mainGuestId;
      this.$refs.newGuestModal.show();
    },
    resetBookingDetails() {
      this.bookingRooms = cloneDeep(this.currentRooms) || [];
      this.bookingGuests = cloneDeep(this.currentGuests) || [];
      this.mainGuestId = this.currentLeadGuest;
      this.currentRooms = null;
      this.currentGuests = null;
      this.currentLeadGuest = null;
    },
    returnToBooking() {
      this.resetBookingDetails();
      this.renderBooking(this.currentBooking);
    },
    createGuest() {
      this.$refs.newGuestModal
        .createContact()
        .then((newContact) => {
          this.$refs.newGuestModal.clearData();

          this.resetBookingDetails();
          this.renderBooking(this.currentBooking);

          if (newContact) {
            const newGuest = {
              id: newContact.id,
              label: `${newContact.first_name} ${newContact.last_name}`,
              image: newContact.photo_md_uri || newContact.photo_uri,
            };

            this.bookingGuests.push(newGuest);
            this.addGuest(newGuest);
          }
        })
        .catch((error) => {
          this.errorNotification("Failed to add contact");
          this.returnToBooking();
        });
    },
    addGuest(contact) {
      const vm = this;
      this.config.title = this.bookingGuests[0]
        ? this.bookingGuests[0].label
        : contact.label;

      this.config.pictureUrl = this.bookingGuests[0]
        ? this.bookingGuests[0].image
        : contact.image;

      if (!this.formData.id) {
        if (!this.mainGuestId) this.mainGuestId = contact.id;
        return; // auto-saving newly added guests is only for existing bookings
      }

      const guest_id = contact.id;
      const booking_id = this.booking.id;

      this.$Progress.start();
      this.addGuestToBooking({
        guest_id,
        booking_id,
      }).then((response) => {
        this.countGuests();

        const guest = this.mergeGuestDetails(response);
        if (!vm.mainGuestId) vm.setMainGuest(guest); // this is the first guest, they're the lead

        this.infoNotification(
          `${guest.label} ` +
            this.$t("booking_modal.notification_guest_added_to_reservation")
        );
        this.$Progress.finish();
      });
    },
    addRoom(room) {
      if (this.booking && this.booking.id) {
        const newReservation = {
          unit_id: room.id,
          booking_id: this.booking.id,
          starts_at: this.resolveSubmitDate(this.formData.starts_at),
          ends_at: this.resolveSubmitDate(this.formData.ends_at),
          guest_ids: this.bookingGuests.map((guest) => guest.id),
        };

        this.$Progress.start();
        this.loadingUnits = true;

        this.addReservation(newReservation)
          .then((response) => {
            room["reservation_id"] = response.id;
            this.infoNotification(
              `${room.label} successfully added to  ${this.booking.name}'s reservation`
            );
            this.prepareTaggregators(response);
            this.populateBooking(response);
          })
          .catch((err) => {
            this.errorNotification(err || "Add reservation error!");
          })
          .finally(() => {
            this.$Progress.finish();
            this.loadingUnits = false;
          });
      } else {
        this.prepareTaggregators();
      }
    },
    removeRoom(room, success) {
      if (!success) {
        this.errorNotification(
          `Failed to remove ${room.label}, all bookings need at least one room.`
        );
        return;
      }
      if (this.booking && this.booking.id) {
        const targetedReservation = this.booking.reservations.find(
          (reservation) => reservation.unit_id === room.id
        );

        this.$Progress.start();
        this.removeReservation(targetedReservation)
          .then((removedReservation) => {
            this.$Progress.finish();
            this.infoNotification(
              `${room.label} successfully removed from ${this.booking.name}'s reservation`
            );
            this.prepareTaggregators(targetedReservation);
            this.populateBooking(targetedReservation);
          })
          .catch((err) => {
            this.errorNotification(err || "Remove reservation error!");
          })
          .finally(() => {
            this.$Progress.finish();
          });
      } else {
        this.prepareTaggregators();
      }
    },
    removeGuest(guest, success) {
      if (this.formData.id && !success) {
        this.errorNotification(
          "Each booking must have at least 1 guest attached."
        );
        return;
      }

      if (!this.formData.id && this.bookingGuests.length === 1) {
        this.bookingGuests = [];
        this.config.title = "";
        this.config.pictureUrl = "";
        this.mainGuestId = null;
        return;
      }

      this.$Progress.start();
      const guestBooking = _.remove(
        this.bookingGuests,
        (bookedGuest) => bookedGuest.id === guest.id
      )[0];
      this.countGuests();

      if (guest.id === this.mainGuestId)
        this.setMainGuest(this.bookingGuests[0]);

      if (this.formData.id) {
        this.removeGuestFromBooking({
          guestBooking,
          newBookingName: this.bookingTitle,
        }).then((_response) => {
          this.notifyGuestRemoval(guest.label);
        });
      } else {
        this.bookingGuests.filter((bookedGuest) => bookedGuest.id !== guest.id);
        this.notifyGuestRemoval(guest.label);
      }
    },
    notifyGuestRemoval(guestName) {
      this.nameBooking();
      this.countGuests();
      this.infoNotification(
        `${guestName} ` +
          this.$t("booking_modal.notification_guest_removed_from_reservation")
      );
      this.$Progress.finish();
    },
    populateBooking(reservation) {
      this.booking = this.getBookings.find(
        (booking) => booking.id === reservation.booking_id
      );

      // Check if booking exists and has necessary data
      if (!this.booking) {
        console.error("Booking not found:", reservation.booking_id);
        return;
      }

      this.formData.notes = this.booking.notes;
      this.bookingStatus = this.booking.booking_status_id;

      const guest = this.getContact(this.booking.guest_id);
      this.config.pictureUrl = guest
        ? guest.photo_md_uri || guest.photo_uri
        : "";

      this.populatePaymentData(reservation);
      this.listBookedRooms(reservation);
    },
    populatePackageInfo() {
      this.togglePackages(!!this.booking.package_id);
    },
    nameBooking() {
      if (this.booking.name) {
        this.config.title = this.booking.name;
        return;
      }

      this.resetBookingName();
    },
    resetBookingName() {
      if (this.mainGuestId) {
        let guest = this.bookingGuests.find(
          (guest) => guest.id === this.mainGuestId
        );
        this.config.title = guest ? guest.label : "Unknown Guest";
        return;
      }

      this.config.title = this.bookingGuests[0]
        ? this.bookingGuests[0].label
        : "";
    },
    countGuests() {
      this.guestCount = this.booking.guest_count || this.bookingGuests.length;
    },
    populatePaymentData(reservation) {
      if (!this.booking) {
        console.error("Cannot populate payment data: booking is undefined");
        this.paymentsLoading = false;
        return;
      }

      this.mainGuestId = this.booking.guest_id;
      const booking_id = this.booking.id;

      if (!booking_id || !this.mainGuestId) {
        console.error(
          "Cannot populate payment data: missing booking_id or guest_id",
          {
            booking_id,
            guest_id: this.mainGuestId,
          }
        );
        this.paymentsLoading = false;
        return;
      }

      this.paymentsLoading = true;
      // Add a timeout to prevent endless loading
      const timeoutPromise = new Promise((resolve) => {
        setTimeout(() => {
          resolve({ error: "Timed out fetching tab data" });
        }, 10000); // 10 second timeout
      });

      Promise.race([
        this.getFilteredTab({
          booking_id,
          guest_id: this.mainGuestId,
        }),
        timeoutPromise,
      ])
        .then((bookingTab) => {
          if (bookingTab && bookingTab.error) {
            console.error("Tab data fetch timed out");
            this.paymentsLoading = false;
            this.loadingTimedOut = true;
            return;
          }

          console.log("Tab data loaded:", bookingTab);
          this.bookingTab = bookingTab || {};

          // If the booking tab doesn't have an ID but we have a booking ID, try to recover
          if (!this.bookingTab.id && booking_id) {
            console.warn(
              "No tab ID found but booking ID exists, setting fallback values"
            );
            this.bookingTab = {
              ...this.bookingTab,
              id: `tab-${booking_id}`,
              stats: this.bookingTab.stats || {
                balance_due: 0,
                card_hold: 0,
              },
            };
          }

          this.paymentsLoading = false;
        })
        .catch((error) => {
          console.error("Error fetching tab data:", error);
          this.paymentsLoading = false;
          this.loadingTimedOut = true;

          // Set a fallback tab object with minimal data
          this.bookingTab = {
            id: `tab-${booking_id}`,
            guest_id: this.mainGuestId || null,
            stats: {
              balance_due: 0,
              card_hold: 0,
              subtotal: 0,
            },
          };
        });
    },
    listBookedRooms(reservation) {
      this.bookingRooms = [];

      // Check if booking and reservations exist
      if (!this.booking || !this.booking.reservations) {
        console.error("No reservations found in booking:", this.booking);
        return;
      }

      console.log("Reservations found:", this.booking.reservations.length);

      this.booking.reservations.forEach((bookedReservation) => {
        this.reserveRoom(bookedReservation);
      });
    },
    reserveRoom(reservation) {
      // Check if reservation has unit_id
      if (!reservation || !reservation.unit_id) {
        console.error("Invalid reservation or missing unit_id:", reservation);
        return;
      }

      console.log("Processing reservation for unit:", reservation.unit_id);

      // Find the room in allRooms
      const roomMatch = this.allRooms.find(
        (room) => room.id === reservation.unit_id
      );

      if (!roomMatch) {
        console.warn(
          "Room not found in allRooms for unit_id:",
          reservation.unit_id
        );

        // Try to get the unit directly from getUnits if allRooms doesn't have it
        const unitFromStore = this.getUnits.find(
          (unit) => unit.id === reservation.unit_id
        );

        if (unitFromStore) {
          console.log("Found unit in store instead:", unitFromStore.unit_name);
          const unitType = this.getUnitTypesList.find(
            (unitType) => unitType.id === unitFromStore.unit_type_id
          );

          const room = {
            id: unitFromStore.id,
            label: unitFromStore.unit_name,
            rate: unitFromStore.unit_rate,
            unitType: unitType ? unitType.unit_type_name : "Unknown",
            startDate: reservation.starts_at,
            endDate: reservation.ends_at,
            doorCode: reservation.vk_door_code,
            rateType: reservation.rate_type_id,
            reservation_id: reservation.id,
          };

          console.log("Adding room to booking rooms:", room.label);
          this.bookingRooms.push(room);
        } else {
          console.error(
            "Unit not found in store either for unit_id:",
            reservation.unit_id
          );
        }
        return;
      }

      const room = {
        ...roomMatch,
        startDate: reservation.starts_at,
        endDate: reservation.ends_at,
        doorCode: reservation.vk_door_code,
        rateType: reservation.rate_type_id,
        reservation_id: reservation.id,
      };

      console.log("Adding room to booking rooms:", room.label);
      this.bookingRooms.push(room);
    },
    populateForm(reservation) {
      this.formData = {
        ...this.formData,
        ...reservation,
      };

      // format times
      this.formData.starts_at = this.$moment(reservation.starts_at).toDate();
      this.formData.ends_at = this.$moment(reservation.ends_at).toDate();

      this.formData.notes = reservation.booking_notes;

      delete reservation.booking_notes;

      // default date to start newly added rooms at
      this.newReservationStart = reservation.starts_at; // formatBookingPeriod(reservation.starts_at);
      this.newReservationEnd = reservation.ends_at; // formatBookingPeriod(reservation.ends_at);
    },
    populateGuests() {
      if (!this.booking.guests) this.booking.guests = [];
      this.bookingGuests = this.contacts
        .filter(
          (contact) =>
            this.booking.guests
              .map((guest) => guest.guest_id)
              .indexOf(contact.id) !== -1
        )
        .map((contact) => {
          const guest = this.booking.guests.find(
            (guest) => guest.guest_id === contact.id
          );
          return {
            ...contact,
            guestBookingId: guest.id,
            bookingId: this.booking.id,
          };
        });

      this.countGuests();
      this.nameBooking();
    },
    mergeGuestDetails(guest) {
      // a guest has data regarding the reservation, a contact has all user info
      const bookedContact = this.contacts.find(
        (contact) => contact.id === guest.guest_id
      );
      const bookedGuest = this.bookingGuests.find(
        (guest) => guest.id === bookedContact.id
      );
      bookedGuest.guestBookingId = guest.id;
      bookedGuest.bookingId = guest.booking_id;

      return bookedGuest;
    },
    addDuration(reservation) {
      reservation.duration = this.$moment(reservation.ends_at).diff(
        this.$moment(reservation.starts_at),
        "days"
      );
    },
    reservationCost(reservation) {
      const unitRate =
        !isNaN(parseFloat(reservation.unit_rate)) &&
        isFinite(reservation.unit_rate)
          ? reservation.unit_rate
          : reservation.unit.unit_rate;
      return reservation.duration * unitRate;
    },
    async prepareTaggregators(reservation = this.reservationState) {
      this.contacts = this.taggregateGuests(cloneDeep(this.getContacts));
      const { starts_at, ends_at } = reservation;

      this.loadingUnits = true;
      this.loadingTimedOut = false;

      // Set a timeout to force the loading state to complete if it takes too long
      const loadingTimeout = setTimeout(() => {
        if (this.loadingUnits) {
          console.warn("Loading units timed out after 15 seconds");
          this.loadingUnits = false;
          this.loadingTimedOut = true;
        }
      }, 15000);

      try {
        await this.fetchAvailableUnitsByDate({
          starts_at,
          ends_at,
        });

        // Update rooms for the taggregator
        this.rooms = this.taggregateUnits(
          cloneDeep(this.getAvailableUnitsByDate),
          starts_at,
          ends_at
        );

        // Make sure allRooms is updated with complete unit data
        if (!this.allRooms.length) {
          this.allRooms = this.taggregateUnits(cloneDeep(this.getUnits));
        }
      } catch (error) {
        console.error("Error fetching available units:", error);
        this.loadingTimedOut = true;
      } finally {
        clearTimeout(loadingTimeout);
        this.loadingUnits = false;
      }
    },
    taggregateGuests(guests) {
      return guests.map((guest) => {
        return {
          id: guest.id,
          label: `${guest.first_name} ${guest.last_name}`,
          image: guest.photo_md_uri || guest.photo_uri,
          email: guest.email,
          phone: guest.phone,
        };
      });
    },
    taggregateUnits(units, starts_at = null, ends_at = null) {
      return units
        .map((unit) => {
          const unitType = this.getUnitTypesList.find(
            (unitType) => unitType.id === unit.unit_type_id
          );

          if (!unitType) {
            return null; // orphaned unit
          }

          let total_rate =
            starts_at && ends_at
              ? this.getReservationRate(
                  unit.id,
                  unit.unit_rate,
                  starts_at,
                  ends_at
                )
              : unit.unit_rate;

          return {
            id: unit.id,
            label: unit.unit_name,
            rate: unit.unit_rate,
            total_rate: total_rate,
            unitType: unitType.unit_type_name,
          };
        })
        .filter((unit) => unit);
    },
    deleteBooking() {
      this.$refs.confirm.open();
    },
    confirmedDeleteBooking() {
      this.removeBooking(this.formData.booking_id);
      this.successNotification("Booking succesfully deleted");
      this.hide();
    },
    cancelDeleteBooking() {
      this.$refs.confirm.close();
    },
    resolveDisplayDate(date) {
      return this.$moment(date).format("MM/DD/YYYY");
    },
    resolveSubmitDate(date) {
      return this.$moment(date).format("YYYY-MM-DD");
    },
    showEmptyModal() {
      this.$refs.modal.show();
    },
    blankForm() {
      return {
        id: null,
        name: null,
        starts_at: null,
        ends_at: null,
        notes: null,
        rate: null,
        reservations: [],
        booking_status_id: 1,
        booking_id: null,
        booking_name: null,
        booking_rate_type_id: 1,
      };
    },
    setMainGuest(guest) {
      if (this.mainGuestId === guest.id) return;

      this.mainGuestId = guest.id;
      this.config.title = guest.label;
      this.config.pictureUrl = guest.image;

      if (this.booking.id) {
        this.updateBooking({
          id: this.booking.id,
          guest_id: this.mainGuestId,
          name: this.bookingTitle,
        }).then(
          this.infoNotification(`${guest.label} is now the booking owner`)
        );
      } else {
        this.infoNotification(`${guest.label} is now the booking owner`);
      }
    },
    onSubmit(evt) {
      evt.preventDefault();
    },
    saveBooking() {
      if (this.bookingGuests < 1 || this.bookingRooms < 1) {
        this.errorNotification(
          "Bookings require at least one guest and one room"
        );
        return;
      }

      if (!this.mainGuestId) {
        this.errorNotification(
          "Bookings require one guest to be selected as the lead"
        );
        return;
      }

      this.config.title = this.config.title || this.bookingGuests[0].label;

      const booking_data = this.formattedBooking();

      if (this.formData.booking_id) {
        return this.updateBooking(booking_data)
          .then((res) => {
            this.successNotification("Successfully updated booking");
            this.hide();
          })
          .catch((error) => {
            this.errorNotification(error || "Update booking error");
          });
      }

      this.$Progress.start();
      this.$store
        .dispatch("addBooking", booking_data)
        .then((res) => {
          this.successNotification("Successfully created booking");
          this.hide();
        })
        .catch((error) => {
          this.errorNotification(error || "Create booking error");
        })
        .finally(() => {
          this.$Progress.finish();
        });
    },
    isMainGuest(selection) {
      return selection.id === this.mainGuestId;
    },
    formattedBooking() {
      let packageRate, package_id, rate;

      const starts_at = this.resolveSubmitDate(this.formData.starts_at);
      const ends_at = this.resolveSubmitDate(this.formData.ends_at);

      const { booking_status_id, booking_rate_type_id, notes } = this.formData;

      rate = this.formData.rate;
      const name = this.bookingTitle;
      const guest_id =
        this.mainGuestId ||
        this.bookingGuests.map((bookingGuest) => bookingGuest.id)[0];
      const guest_count = this.guestCount || this.bookingGuests.length;

      if (this.packageEnabled) {
        package_id = this.$refs.packageForm.groupBookingType;
        rate = this.$refs.packageForm.groupBookingPrice;
      } else {
        package_id = null;
      }

      const booking = {
        name,
        starts_at,
        ends_at,
        booking_rate_type_id,
        notes,
        // paid_deposit,
        guest_id,
        guest_count,
        rate,
        package_id,
      };

      if (!this.formData.id) {
        booking.guest_ids = this.bookingGuests.map(
          (bookingGuest) => bookingGuest.id
        );

        // invalid to update reservations
        booking.reservations = this.bookingRooms
          .filter((room) => !!room.id)
          .map((room) => {
            return {
              unit_id: room.id,
              starts_at: this.resolveSubmitDate(room.startDate),
              ends_at: this.resolveSubmitDate(room.endDate),
            };
          });
      } else {
        booking.id = this.formData.booking_id;
      }

      return booking;
    },
    getRoomDateRange(room) {
      room.startDate = room.startDate || this.newReservationStart;
      room.endDate = room.endDate || this.newReservationEnd;

      return {
        start: this.$moment(room.startDate, "YYYY-MM-DD"),
        end: this.$moment(room.endDate, "YYYY-MM-DD"),
      };
    },
    getDisabledDates(unit_id, selection) {
      let start_date =
        selection && selection.startDate
          ? selection.startDate.toString().substring(0, 10)
          : "";
      let end_date =
        selection && selection.endDate
          ? selection.endDate.toString().substring(0, 10)
          : "";
      let reserved_dates = this.getReservedDates[unit_id] || [];

      reserved_dates = reserved_dates.map((_date) => {
        let _start = this.$moment(_date.start, "YYYY-MM-DD");
        let _end = this.$moment(_date.end, "YYYY-MM-DD");

        _start = _date.start == end_date ? _start.add(1, "day") : _start;

        _end = _date.end == start_date ? _end.subtract(1, "day") : _end;

        return {
          start: _start,
          end: _end,
        };
      });

      return reserved_dates;
    },
    setRoomDateRange(dates, room) {
      let startDate = this.resolveSubmitDate(dates.start);
      let endDate = this.resolveSubmitDate(dates.end);

      if (startDate === endDate) {
        return;
      }

      room.startDate = startDate;
      room.endDate = endDate;

      if (this.booking && this.booking.id) {
        let reservation_id = -1;
        let reservation = this.booking.reservations.find(
          (reservation, index) => {
            if (reservation.unit_id === room.id) {
              reservation_id = index;
              return true;
            }
          }
        );

        if (!reservation) {
          return;
        }

        let updated_reservation = Object.assign({}, reservation); // JSON.parse(JSON.stringify(reservation));

        updated_reservation.starts_at = startDate;
        updated_reservation.ends_at = endDate;

        this.$Progress.start();
        this.loadingUnits = true;

        this.updateReservation(updated_reservation)
          .then((res) => {
            this.booking.reservations[reservation_id] = res;
            this.infoNotification(
              "Reservation has been updated with new dates."
            );
            this.populateBooking(res);
          })
          .catch((_err) => {
            let err_message =
              _err || "We were unable to update this reservation";
            this.errorNotification(err_message);

            room.startDate = reservation.starts_at;
            room.endDate = reservation.ends_at;
          })
          .finally(() => {
            this.$Progress.finish();
            this.loadingUnits = false;
          });
      }
    },
    formatBookingPeriod,
    formatMoney(amount) {
      if (amount === null || amount === undefined)
        return this.getVendor.local_currency + "0.00";
      return this.getVendor.local_currency + parseFloat(amount).toFixed(2);
    },
  },

  watch: {
    getContacts: {
      handler(newContacts, oldContacts) {
        this.contacts = this.taggregateGuests(newContacts);
      },
      deep: true,
    },
  },
};
</script>

<style lang="scss">
.booking--locked {
  .booking-action-btn,
  .main-guest-button,
  .align-room-select-right,
  .remove-selection {
    pointer-events: none;
    opacity: 0.5;
  }

  .booking-room-list,
  .active-toggle {
    pointer-events: none;
  }
}

.reservation-rate {
  cursor: pointer;
}

div#go-to-payments {
  position: absolute;
  right: 20px;
  top: 30px;
}

.card {
  /* max-height: 6rem; */
  // overflow: hidden;
  overflow: visible;
  margin: 20px 0;
}

.card-img {
  opacity: 0.2;
  border: 2px solid black;
}

.btn-circle {
  width: 30px;
  height: 30px;
  padding: 6px 0px;
  border-radius: 15px;
  text-align: center;
  font-size: 12px;
  line-height: 1.42857;
}

.btn-circle.btn-xl {
  width: 70px;
  height: 70px;
  padding: 10px 16px;
  border-radius: 35px;
  font-size: 24px;
  line-height: 1.33;
}

.card.dotted {
  border: 1px dashed;
}

.add-card {
  background: #f9f9f9;
  max-height: 50px;
  padding: 10px;
  cursor: pointer;
}

.add-card > .card-body {
  padding-top: 5px;
}

form.booking-form {
  margin-top: 25px;
}

.room-card {
  span {
    margin: 5px 6%;
    text-transform: capitalize;

    .reservation-sub-label {
      margin: initial;
      font-style: italic;
      text-transform: initial;
    }
  }
}

.result-sub-label {
  font-size: 14px;
}

.result-right-label {
  float: right;
  margin-right: 25px;
}

.main-guest-button {
  float: left;
  padding: 0px 6px;
  background-color: #fff;
}

.phone-guest-button {
  padding: 0px 6px;
}

.door-lock-button {
  padding: 0px 6px;
}

.active {
  background-color: #000000a3;

  i.fi.fi-crown.small {
    color: gold;
  }
}

.guest-count-row {
  right: 20px;
  display: flex;
  position: absolute;
  top: 20px;
  color: black;

  .entry-info {
    margin-top: 15px;
    padding-right: 25px;
  }

  span.entry-label-text {
    color: black;
    width: 100px;
    top: -10px;
    left: 50px;
  }

  #guest-count {
    width: 100px;
  }
}

.reservation-door-code-text {
  font-size: 12pt;
  margin: 5px !important;
}

.reservation-dates {
  position: relative;
  margin: 5px 0px;

  .daterange-picker {
    display: inline-block;
    position: absolute;
    left: 0;
    width: 100%;

    .c-day-content {
      width: 2rem !important;
    }

    input {
      border: none;
      opacity: 0;
    }
  }
}

.reservation-door-code {
  margin: 5px !important;

  i {
    font-size: 10pt;
  }
}

.section-border {
  border: 1px solid black;
  padding: 25px;
  background: white;
  margin-top: 25px;
}

.status-icon {
  cursor: pointer;
  height: 26px;
  width: 24px;
  margin-top: -8px;
  margin-right: -2px;
  padding-top: 4px;
}

.booking-status {
  &.disabled {
    .check-in-icon,
    .check-out-icon {
      color: #999 !important;
      cursor: default;
    }
  }
}

.check-in-icon {
  color: #8ae88a;

  &:hover {
    color: #6ac36a;
  }
}

.check-out-icon {
  color: #f79696;

  &:hover {
    color: #de6969;
  }
}

.align-room-select-right {
  position: absolute;
  right: 3rem;
  top: 4px;
}

.card-body {
  text-align: left;
}

.room-card {
  display: inline-block !important;
  width: 90%;
}

.room-label {
  position: absolute;
  left: 30px;
  margin: 0px !important;
}

.reservation-rate {
  position: absolute;
  margin: 0px !important;
  right: 50px;
}

#booking_notes {
  padding-right: 80px;
}

.note-button {
  position: absolute;
  right: 30px;
  top: 85px;
}
</style>
